import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme, type ThemeMode } from '@/contexts/ThemeContext';
import { useUsage } from '@/contexts/UsageContext';
import { getSavedLessonIds } from '@/mocks/historyStore';
import { appearanceOptions } from '@/mocks/profile';
import { getScanHistory } from '@/mocks/scanHistory';
import { getStudySessions } from '@/mocks/studySessions';
import AccountPanel from './components/AccountPanel';
import AboutPanel from './components/AboutPanel';
import HelpSupportPanel from './components/HelpSupportPanel';
import { calculateProfileStatistics } from './profileStatistics';

type ActivePanel = 'account' | 'help' | 'about' | null;

interface ProfileActionRowProps {
  icon: string;
  title: string;
  subtitle?: string;
  onClick: () => void;
  isDark: boolean;
  muted?: boolean;
}

function ProfileActionRow({ icon, title, subtitle, onClick, isDark, muted = false }: ProfileActionRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors ${
        isDark ? 'hover:bg-white/[0.055] focus-visible:ring-white/40' : 'hover:bg-black/[0.035] focus-visible:ring-black/20'
      } focus-visible:outline-none focus-visible:ring-2`}
    >
      <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl border ${
        isDark ? 'border-white/[0.07] bg-white/[0.035]' : 'border-black/[0.06] bg-black/[0.025]'
      } ${muted ? (isDark ? 'text-white/45' : 'text-neutral-500') : (isDark ? 'text-white/75' : 'text-neutral-700')}`}>
        <i aria-hidden="true" className={`${icon} text-[17px]`} />
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block text-sm font-semibold ${muted ? (isDark ? 'text-white/70' : 'text-neutral-600') : (isDark ? 'text-white/90' : 'text-neutral-900')}`}>
          {title}
        </span>
        {subtitle && (
          <span className={`mt-0.5 block text-xs leading-4 ${isDark ? 'text-white/45' : 'text-neutral-500'}`}>
            {subtitle}
          </span>
        )}
      </span>
      <i aria-hidden="true" className={`ri-arrow-right-s-line text-lg transition-transform group-hover:translate-x-0.5 ${
        isDark ? 'text-white/30 group-hover:text-white/65' : 'text-neutral-300 group-hover:text-neutral-600'
      }`} />
    </button>
  );
}

function formatJoinedDate(value?: string) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(date);
}

function formatDate(value?: string) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'S';
  return parts.slice(0, 2).map((part) => part[0].toUpperCase()).join('');
}

export default function Profile() {
  const navigate = useNavigate();
  const { user, isRehydrated, signOut } = useAuth();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { generationAccess, isLoading: usageLoading, accessError } = useUsage();
  const isDark = resolvedTheme === 'dark';
  const userId = user?.id;
  const [activePanel, setActivePanel] = useState<ActivePanel>(null);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const [signOutError, setSignOutError] = useState('');
  const [stats, setStats] = useState<{
    ownerId: string;
    generatedLessons: number;
    savedLessons: number;
  } | null>(null);
  const preferencesRef = useRef<HTMLElement | null>(null);

  const displayName = user?.user_metadata.display_name?.trim()
    || user?.email.split('@')[0]
    || 'Your Profile';
  const joinDate = formatJoinedDate(user?.created_at);
  const avatarUrl = useMemo(() => {
    const candidate = user?.user_metadata.avatar_url || user?.user_metadata.picture;
    if (!candidate) return null;
    try {
      const url = new URL(candidate);
      return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : null;
    } catch {
      return null;
    }
  }, [user?.user_metadata.avatar_url, user?.user_metadata.picture]);

  const refreshStats = useCallback(() => {
    if (!userId || !isRehydrated) {
      setStats(null);
      return;
    }
    setStats({
      ownerId: userId,
      ...calculateProfileStatistics(
      getScanHistory(),
      getStudySessions().map((session) => session.id),
      getSavedLessonIds(),
      ),
    });
  }, [userId, isRehydrated]);

  useEffect(() => {
    refreshStats();
    window.addEventListener('lavyn-account-save', refreshStats);
    window.addEventListener('focus', refreshStats);
    window.addEventListener('storage', refreshStats);
    return () => {
      window.removeEventListener('lavyn-account-save', refreshStats);
      window.removeEventListener('focus', refreshStats);
      window.removeEventListener('storage', refreshStats);
    };
  }, [refreshStats]);

  const accessVerified = Boolean(user && !usageLoading && !accessError && generationAccess);
  const isPremium = accessVerified && generationAccess?.tier === 'PREMIUM';
  const planLabel = accessVerified
    ? (isPremium ? 'Premium learner' : 'Free learner')
    : usageLoading
      ? 'Checking membership'
      : accessError
        ? 'Membership unavailable'
        : 'Sign in to view membership';
  const remainingVideos = accessVerified ? generationAccess!.remainingVideos : null;
  const periodLimit = accessVerified ? generationAccess!.periodLimit : null;
  const periodUsed = accessVerified ? generationAccess!.periodUsed : null;
  const unlimitedVideos = accessVerified && generationAccess?.unlimited === true;
  const progress = !unlimitedVideos && periodLimit && periodUsed !== null
    ? Math.min(100, Math.max(0, (periodUsed / periodLimit) * 100))
    : 0;
  const resetDate = accessVerified ? formatDate(generationAccess?.periodResetAt) : null;
  const visibleStats = stats?.ownerId === userId ? stats : null;

  const openPanel = (panel: ActivePanel | 'premium') => {
    if (panel === 'premium') {
      navigate('/profile/premium');
      return;
    }
    if (panel === 'account' && !user) {
      navigate('/auth/login');
      return;
    }
    setActivePanel(panel);
  };

  const handleSignOut = async () => {
    setSignOutError('');
    const result = await signOut();
    if (result.error) {
      setSignOutError('Sign out could not be completed. Please try again.');
      return;
    }
    navigate('/');
  };

  const shell = isDark
    ? 'border-white/[0.08] bg-[#101011] text-white shadow-[0_24px_80px_rgba(0,0,0,0.2)]'
    : 'border-black/[0.07] bg-white text-neutral-950 shadow-[0_20px_60px_rgba(0,0,0,0.045)]';
  const primaryText = isDark ? 'text-white' : 'text-neutral-950';
  const secondaryText = isDark ? 'text-white/48' : 'text-neutral-500';
  const divider = isDark ? 'bg-white/[0.075]' : 'bg-black/[0.07]';

  return (
    <div className={`study-profile min-h-full px-4 pt-5 pb-32 transition-colors duration-300 sm:px-7 sm:py-8 sm:pb-10 ${
      isDark ? 'bg-[#0b0b0c]' : 'bg-[#f6f6f6]'
    }`}>
      <main className={`mx-auto w-full max-w-3xl overflow-hidden rounded-[26px] border backdrop-blur-xl ${shell}`}>
        <header className="flex items-center justify-between px-5 pb-4 pt-5 sm:px-7 sm:pt-7">
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={() => navigate('/')}
              aria-label="Back to Home"
              className={`grid h-8 w-8 place-items-center rounded-full transition hover:bg-white/[0.06] sm:hidden ${
                isDark ? 'text-white/50' : 'text-neutral-500'
              }`}
            >
              <i aria-hidden="true" className="ri-arrow-left-line text-lg" />
            </button>
            <div>
              <p className={`text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-white/35' : 'text-neutral-400'}`}>
                LAVYN · YOUR SPACE
              </p>
              <h1 className={`mt-1 text-2xl font-semibold tracking-[-0.04em] ${primaryText}`}>Profile</h1>
            </div>
          </div>
          <button
            type="button"
            aria-label="Go to preferences"
            onClick={() => {
              setAppearanceOpen(true);
              preferencesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            className={`grid h-10 w-10 place-items-center rounded-full border transition ${
              isDark ? 'border-white/[0.08] bg-white/[0.035] text-white/60 hover:bg-white/[0.08]' : 'border-black/[0.07] bg-black/[0.025] text-neutral-600 hover:bg-black/[0.055]'
            }`}
          >
            <i aria-hidden="true" className="ri-settings-3-line text-lg" />
          </button>
        </header>

        <button
          type="button"
          onClick={() => openPanel('account')}
          className={`flex w-full items-center gap-4 px-5 pb-6 text-left transition-colors sm:px-7 sm:pb-7 ${
            isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-black/[0.015]'
          }`}
          aria-label={user ? 'Open account details' : 'Sign in to your account'}
        >
          <span className={`relative grid h-[68px] w-[68px] shrink-0 place-items-center overflow-hidden rounded-full border text-lg font-semibold sm:h-[76px] sm:w-[76px] ${
            isDark ? 'border-white/[0.08] bg-[#242426] text-white/80' : 'border-black/[0.07] bg-neutral-100 text-neutral-700'
          }`}>
            {initials(displayName)}
            {avatarUrl && (
              <img
                src={avatarUrl}
                alt=""
                className="absolute inset-0 h-full w-full object-cover"
                referrerPolicy="no-referrer"
                onError={(event) => { event.currentTarget.style.display = 'none'; }}
              />
            )}
          </span>
          <span className="min-w-0 flex-1">
            <span className={`block truncate text-lg font-semibold tracking-[-0.035em] ${primaryText}`}>{displayName}</span>
            <span className="mt-1.5 flex flex-wrap items-center gap-2">
              <span className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${
                isDark ? 'border-white/[0.1] bg-white/[0.05] text-white/70' : 'border-black/[0.08] bg-black/[0.035] text-neutral-600'
              }`}>
                {planLabel}
              </span>
              {joinDate && (
                <span className={`text-xs ${secondaryText}`}>Joined {joinDate}</span>
              )}
            </span>
          </span>
          <i aria-hidden="true" className={`ri-arrow-right-s-line text-xl ${isDark ? 'text-white/35' : 'text-neutral-400'}`} />
        </button>

        <div className={`mx-5 h-px sm:mx-7 ${divider}`} />

        <section className="px-5 py-6 sm:px-7 sm:py-7" aria-labelledby="profile-learning-heading">
          <div className="mb-4 flex items-end justify-between">
            <div>
              <p className={`text-[10px] font-semibold uppercase tracking-[0.19em] ${secondaryText}`}>Your progress</p>
              <h2 id="profile-learning-heading" className={`mt-1.5 text-lg font-semibold tracking-[-0.03em] ${primaryText}`}>
                Your learning
              </h2>
            </div>
              <span className={`text-[11px] ${secondaryText}`}>Saved activity</span>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <div className={`min-w-0 rounded-[19px] border p-3 sm:p-[18px] ${
              isDark ? 'border-white/[0.065] bg-[#1b1b1c]' : 'border-black/[0.055] bg-[#f4f4f4]'
            }`}>
              <span className={`flex h-7 w-7 items-center justify-center rounded-full ${
                isDark ? 'bg-white text-[#171717]' : 'bg-neutral-900 text-white'
              }`}>
                <i aria-hidden="true" className="ri-play-fill text-[13px]" />
              </span>
              <p className={`mt-3 text-2xl font-semibold leading-none tracking-[-0.06em] sm:text-3xl ${primaryText}`}>
                {visibleStats ? visibleStats.generatedLessons : '—'}
              </p>
              <p className={`mt-2 text-[10px] leading-4 sm:text-xs ${secondaryText}`}>Lessons generated</p>
            </div>
            <div className={`min-w-0 rounded-[19px] border p-3 sm:p-[18px] ${
              isDark ? 'border-white/[0.065] bg-[#1b1b1c]' : 'border-black/[0.055] bg-[#f4f4f4]'
            }`}>
              <span className={`flex h-7 w-7 items-center justify-center rounded-full border ${
                isDark ? 'border-white/[0.14] text-white/75' : 'border-black/[0.13] text-neutral-700'
              }`}>
                <i aria-hidden="true" className="ri-bookmark-line text-[14px]" />
              </span>
              <p className={`mt-3 text-2xl font-semibold leading-none tracking-[-0.06em] sm:text-3xl ${primaryText}`}>
                {visibleStats ? visibleStats.savedLessons : '—'}
              </p>
              <p className={`mt-2 text-[10px] leading-4 sm:text-xs ${secondaryText}`}>Saved lessons</p>
            </div>
            <div className={`min-w-0 rounded-[19px] border p-3 sm:p-[18px] ${
              isDark ? 'border-white/[0.065] bg-[#1b1b1c]' : 'border-black/[0.055] bg-[#f4f4f4]'
            }`}>
              <span className={`flex h-7 w-7 items-center justify-center rounded-full border ${
                isDark ? 'border-white/[0.14] text-white/75' : 'border-black/[0.13] text-neutral-700'
              }`}>
                <i aria-hidden="true" className="ri-video-line text-[14px]" />
              </span>
              <p className={`mt-3 text-2xl font-semibold leading-none tracking-[-0.06em] sm:text-3xl ${primaryText}`}>
                {unlimitedVideos ? '∞' : accessVerified && remainingVideos !== null ? remainingVideos : '—'}
              </p>
              <p className={`mt-2 text-[10px] leading-4 sm:text-xs ${secondaryText}`}>Videos Remaining</p>
            </div>
          </div>
          {!user && (
            <p className={`mt-3 text-xs ${secondaryText}`}>Sign in to see learning activity saved to your account.</p>
          )}
        </section>

        <div className={`mx-5 h-px sm:mx-7 ${divider}`} />

        <section className="px-5 py-6 sm:px-7 sm:py-7" aria-labelledby="profile-account-heading">
          <SectionHeading id="profile-account-heading" eyebrow="Membership & details" isDark={isDark}>
            Your account
          </SectionHeading>
          <div className={`mt-4 rounded-[20px] border p-4 sm:p-5 ${
            isDark ? 'border-white/[0.075] bg-white/[0.025]' : 'border-black/[0.065] bg-black/[0.018]'
          }`}>
            <div className="flex items-start gap-3">
              <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl border ${
                isDark ? 'border-white/[0.08] bg-white/[0.045] text-white/75' : 'border-black/[0.07] bg-white text-neutral-700'
              }`}>
                <i aria-hidden="true" className="ri-vip-crown-line text-lg" />
              </span>
              <div className="min-w-0 flex-1">
                <p className={`text-sm font-semibold ${primaryText}`}>{accessVerified ? (isPremium ? 'Premium membership' : 'Free membership') : 'Membership status'}</p>
                <p className={`mt-0.5 text-xs ${secondaryText}`}>
                  {accessVerified
                    ? (isPremium ? 'Your Premium learning benefits' : 'Your LAVYN learning plan')
                    : (accessError ? 'Plan details could not be verified right now.' : 'Verified account plan and video allowance')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/profile/premium')}
                aria-label="View membership plans and benefits"
                className={`grid h-9 w-9 shrink-0 place-items-center rounded-full transition ${
                  isDark ? 'text-white/45 hover:bg-white/[0.07] hover:text-white' : 'text-neutral-400 hover:bg-black/[0.04] hover:text-neutral-800'
                }`}
              >
                <i aria-hidden="true" className="ri-arrow-right-s-line text-xl" />
              </button>
            </div>

            {accessVerified && (
              <div className="mt-5">
                <div className="flex items-baseline justify-between gap-3">
                  <p className={`text-xs font-medium ${primaryText}`}>Video allowance</p>
                  <p className={`text-xs ${secondaryText}`}>
                    {unlimitedVideos
                      ? <span className={`font-semibold ${primaryText}`}>Unlimited</span>
                      : <><span className={`font-semibold ${primaryText}`}>{remainingVideos}</span> of {periodLimit} left</>}
                  </p>
                </div>
                {!unlimitedVideos && <div
                  className={`mt-2 h-1.5 overflow-hidden rounded-full ${isDark ? 'bg-white/[0.09]' : 'bg-black/[0.08]'}`}
                  role="progressbar"
                  aria-label="Video allowance used"
                  aria-valuemin={0}
                  aria-valuemax={periodLimit ?? 0}
                  aria-valuenow={periodUsed ?? 0}
                >
                  <div
                    className={`h-full rounded-full transition-[width] duration-300 ${isDark ? 'bg-white/75' : 'bg-neutral-800'}`}
                    style={{ width: `${progress}%` }}
                  />
                </div>}
                <div className={`mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] ${secondaryText}`}>
                  <span>{unlimitedVideos ? 'Unlimited testing access for this account' : generationAccess?.periodType === 'term' ? 'Current term allowance' : 'Current monthly allowance'}</span>
                  {resetDate && <span>{generationAccess?.periodType === 'term' ? 'Ends' : 'Resets'} {resetDate}</span>}
                </div>
                {isPremium ? (
                  <div className={`mt-4 flex flex-wrap gap-2 text-[11px] ${secondaryText}`}>
                    <span className={`rounded-full px-2.5 py-1 ${isDark ? 'bg-white/[0.06]' : 'bg-black/[0.045]'}`}>Ad-free generation</span>
                    <span className={`rounded-full px-2.5 py-1 ${isDark ? 'bg-white/[0.06]' : 'bg-black/[0.045]'}`}>Premium lesson access</span>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => navigate('/profile/premium')}
                    className={`mt-4 inline-flex min-h-10 items-center justify-center gap-2 rounded-xl px-4 text-xs font-semibold transition ${
                      isDark ? 'bg-white text-neutral-950 hover:bg-white/85' : 'bg-neutral-900 text-white hover:bg-neutral-800'
                    }`}
                  >
                    Explore Premium
                    <i aria-hidden="true" className="ri-arrow-right-line text-sm" />
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="mt-2">
            <ProfileActionRow
              icon="ri-user-3-line"
              title="Account details"
              subtitle={user?.email || 'Sign in to manage your account'}
              onClick={() => openPanel('account')}
              isDark={isDark}
            />
          </div>
        </section>

        <div className={`mx-5 h-px sm:mx-7 ${divider}`} />

        <section
          ref={preferencesRef}
          id="profile-preferences"
          className="scroll-mt-6 px-5 py-6 sm:px-7 sm:py-7"
          aria-labelledby="profile-preferences-heading"
        >
          <SectionHeading id="profile-preferences-heading" eyebrow="Make it yours" isDark={isDark}>
            Preferences &amp; support
          </SectionHeading>
          <div className="mt-3 space-y-1">
            <button
              type="button"
              onClick={() => setAppearanceOpen((open) => !open)}
              aria-expanded={appearanceOpen}
              className={`group flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors ${
                isDark ? 'hover:bg-white/[0.055] focus-visible:ring-white/40' : 'hover:bg-black/[0.035] focus-visible:ring-black/20'
              } focus-visible:outline-none focus-visible:ring-2`}
            >
              <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl border ${
                isDark ? 'border-white/[0.07] bg-white/[0.035] text-white/75' : 'border-black/[0.06] bg-black/[0.025] text-neutral-700'
              }`}>
                <i aria-hidden="true" className="ri-palette-line text-[17px]" />
              </span>
              <span className="min-w-0 flex-1">
                <span className={`block text-sm font-semibold ${primaryText}`}>Appearance</span>
                <span className={`mt-0.5 block text-xs ${secondaryText}`}>
                  {theme === 'system' ? 'System Default' : theme === 'dark' ? 'Dark' : 'Light'}
                </span>
              </span>
              <i aria-hidden="true" className={`ri-arrow-down-s-line text-lg transition-transform ${
                appearanceOpen ? 'rotate-180' : ''
              } ${isDark ? 'text-white/35' : 'text-neutral-400'}`} />
            </button>
            {appearanceOpen && (
              <div className={`grid grid-cols-3 gap-2 rounded-2xl border p-2 ${
                isDark ? 'border-white/[0.06] bg-white/[0.025]' : 'border-black/[0.06] bg-black/[0.018]'
              }`} role="group" aria-label="Appearance preference">
                {appearanceOptions.map((option) => {
                  const selected = theme === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setTheme(option.id as ThemeMode)}
                      aria-pressed={selected}
                      className={`flex min-h-11 items-center justify-center gap-1.5 rounded-xl border px-2 py-2 text-[11px] font-semibold transition-colors ${
                        selected
                          ? isDark
                            ? 'border-white bg-white text-neutral-950'
                            : 'border-neutral-900 bg-neutral-900 text-white'
                          : isDark
                            ? 'border-white/[0.07] bg-white/[0.025] text-white/60 hover:bg-white/[0.07]'
                            : 'border-black/[0.06] bg-white text-neutral-600 hover:bg-neutral-100'
                      }`}
                    >
                      <i aria-hidden="true" className={`${option.icon} text-sm`} />
                      {option.label}
                    </button>
                  );
                })}
              </div>
            )}
            <ProfileActionRow
              icon="ri-question-line"
              title="Help & Support"
              onClick={() => openPanel('help')}
              isDark={isDark}
            />
            <ProfileActionRow
              icon="ri-information-line"
              title="About LAVYN"
              onClick={() => openPanel('about')}
              isDark={isDark}
            />
            {user && (
              <ProfileActionRow
                icon="ri-logout-box-r-line"
                title="Sign out"
                onClick={() => void handleSignOut()}
                isDark={isDark}
                muted
              />
            )}
            {signOutError && (
              <p role="alert" className={`px-3 pb-2 text-xs ${isDark ? 'text-white/60' : 'text-neutral-600'}`}>
                {signOutError}
              </p>
            )}
          </div>
        </section>

        <footer className={`flex items-center justify-between gap-3 border-t px-5 py-4 text-[10px] sm:px-7 ${
          isDark ? 'border-white/[0.06] text-white/30' : 'border-black/[0.06] text-neutral-400'
        }`}>
          <span>LAVYN · Learn at your pace</span>
          {user && <span className="truncate">{user.email}</span>}
        </footer>
      </main>

      <AccountPanel open={activePanel === 'account'} onClose={() => setActivePanel(null)} />
      <HelpSupportPanel open={activePanel === 'help'} onClose={() => setActivePanel(null)} />
      <AboutPanel open={activePanel === 'about'} onClose={() => setActivePanel(null)} />
    </div>
  );
}

function SectionHeading({
  id,
  eyebrow,
  children,
  isDark,
}: {
  id: string;
  eyebrow: string;
  children: React.ReactNode;
  isDark: boolean;
}) {
  return (
    <div>
      <p className={`text-[10px] font-semibold uppercase tracking-[0.19em] ${isDark ? 'text-white/40' : 'text-neutral-400'}`}>
        {eyebrow}
      </p>
      <h2 id={id} className={`mt-1.5 text-lg font-semibold tracking-[-0.03em] ${isDark ? 'text-white' : 'text-neutral-950'}`}>
        {children}
      </h2>
    </div>
  );
}
